const conflictSources = require('./timesheetConflictSources');
const {calendarDay, gapHint} = require('./timesheetDayPresentation');
const {groupGaps} = require('./timesheetReviewGaps');
const {orderedRows} = require('./timesheetSourceOrder');

const statuses = Object.freeze({historical_locked: 'Архив', submitted: 'Подан', approved: 'Утверждён', returned: 'Возвращён', hr_editing: 'Исправляет табельщик', draft: 'Черновик'});
const issueLabels = Object.freeze({long_day: 'Длительная смена', daily_maximum: 'Превышен дневной максимум', incompatible_entries: 'Несовместимые отметки', excess_days: 'Превышено число дней', missing_code: 'Неизвестный код', missing_marks: 'Пробел между отметками'});
const formatter = new Intl.NumberFormat('ru-RU', {maximumFractionDigits: 4});
const decimal = value => value == null ? '—' : formatter.format(Number(value));
const hours = value => value == null ? '—' : decimal(value / 60);
function compactName(name) {
    const parts = String(name || '').trim().split(/\s+/);
    if (parts.length < 2 || parts.slice(1).some(part => /[.\d]/.test(part))) return name;
    return `${parts[0]} ${parts.slice(1).map(part => part.charAt(0) + '.').join('')}`;
}
function authorLabel(source) {
    return source.author_id ? `${source.author_role === 'editor' ? 'Последним изменил' : 'Подал табель'}: ${source.author_name}` : source.author_name;
}
function rowValue(row, group, date) {
    const entry = row.days[date];
    if (!entry) return !row.historical && group.days[date].worked_minutes > 0 ? 'Дт' : '';
    if (row.historical && entry.source_value != null) return String(entry.source_value);
    const codes = [entry.absence_code, entry.travel_code, entry.other_code].filter(Boolean);
    return [entry.worked_minutes > 0 || !codes.length ? hours(entry.worked_minutes) : '', ...codes].filter(value => value !== '').join('/');
}
function totalValue(day) { return !day.explicit ? '' : day.worked_minutes > 0 ? hours(day.worked_minutes) : day.codes.join('/') || '0'; }
function codeKind(entry, code) {
    if (entry.travel_code !== code || Number(entry.worked_minutes) > 0) return 'reference';
    return entry.equivalent_days == null ? 'context' : Number(entry.equivalent_days) > 0 ? 'signal' : 'reference';
}
function valueTokens(row, group, date) {
    const entry = row ? row.days[date] : group.days[date];
    if (!entry) return row && row.historical ? [] : group.days[date].worked_minutes > 0 ? [{text: 'Дт', kind: 'reference'}] : [];
    if (row && row.historical && entry.source_value != null) return [{text: String(entry.source_value), kind: 'reference'}];
    if (!row && !entry.explicit) return [];
    const codes = row ? [entry.absence_code, entry.travel_code, entry.other_code].filter(Boolean) : entry.worked_minutes > 0 ? [] : entry.codes;
    const tokens = [];
    if (entry.worked_minutes > 0 || !codes.length) tokens.push({text: hours(entry.worked_minutes), kind: entry.worked_minutes > 0 ? 'signal' : 'reference'});
    codes.forEach(code => {
        if (tokens.length) tokens.push({text: '/', kind: 'reference'});
        const kinds = row ? [codeKind(entry, code)] : group.rows.filter(row => row.days[date]).map(row => codeKind(row.days[date], code));
        tokens.push({text: code, kind: kinds.includes('signal') ? 'signal' : kinds.includes('context') ? 'context' : 'reference'});
    });
    return tokens;
}

// Maps and lazy row caches deliberately live outside Vue's dependency graph.
// A row's title/aria-label share the same string; no global computed is read
// once per cell (Vue 2 would repeatedly subscribe to the entire month's DTO).
function createView(grid) {
    const sources = new Map(grid.sources.map(source => [source.timesheet_id, source]));
    const groups = new WeakMap();
    const sourceOrders = new WeakMap();
    const rows = new Map();
    const columns = Object.freeze(grid.dates.map(date => {
        return Object.freeze({date, label: Number(date.slice(8)), ...calendarDay(date)});
    }));
    function source(row) { return sources.get(row.source_id); }
    function conflictSource(row, date) {
        const value = source(row);
        return {...row.days[date], work_date: date, timesheet_id: value.timesheet_id, line_id: row.line_id, review_revision: value.revision_id,
            object_visible: true, object: {name: [value.object_name, value.name].filter(Boolean).join(' · ')}};
    }
    function groupView(group) {
        if (!groups.has(group)) {
            const days = {};
            const gaps = grid.historical ? new Map() : groupGaps(grid, group, sources);
            columns.forEach(column => {
                const date = column.date, day = group.days[date];
                const descriptions = group.rows.filter(row => row.days[date]).map(row => conflictSources.text(conflictSource(row, date)));
                days[date] = {suffix: `${day.issues.map(item => item.message).join(' ')} ${descriptions.join(' ')}`,
                    gap: gaps.has(date), gapHint: gapHint(gaps.get(date), date, 'Доступные табели сотрудника'),
                    className: [column.weekend && 'weekend', day.issues.length && 'has-review-issue', ...['warning', 'attention', 'blocking'].filter(level => day.issues.some(item => item.level === level))].filter(Boolean).join(' ')};
            });
            groups.set(group, {name: compactName(group.name), days, gaps, excess: group.issues.some(item => item.type === 'excess_days')});
        }
        return groups.get(group);
    }
    function rowView(item) {
        if (rows.has(item.key)) return rows.get(item.key);
        const {group, row} = item, total = !row, common = groupView(group), origin = row && source(row);
        const totals = row ? row.totals : group.totals;
        const result = Object.freeze({...item, total, name: row && row.historical ? row.name : common.name, excess: common.excess, source: origin,
            objectName: origin ? [origin.object_name, origin.name].filter(Boolean).join(' · ') : 'Все табели',
            objectTitle: origin ? [origin.object_name, origin.name, authorLabel(origin), statuses[origin.status],
                origin.is_draft ? 'Текущие сохранённые данные, ещё не подан' : `Версия ${origin.revision_number}`,
                origin.on_correction ? 'На исправлении — показана поданная версия' : ''].filter(Boolean).join(' · ')
                : `Все табели сотрудника, замечаний: ${group.issues.length}${common.gaps.size ? `. Дней с пробелами между отметками: ${common.gaps.size} (информационное замечание)` : ''}`,
            status: origin && (origin.status === 'hr_editing' ? 'На правке' : statuses[origin.status] || origin.status),
            authorTitle: origin && authorLabel(origin), author: origin && (origin.author_id ? compactName(origin.author_name) : origin.author_name),
            daysTotal: decimal(totals.equivalent_days), hoursTotal: hours(totals.worked_minutes),
            cells: columns.map(({date}) => {
                const day = group.days[date], value = row ? rowValue(row, group, date) : totalValue(day);
                return {date, value, tokens: valueTokens(row, group, date), title: `${group.name}, ${date}: ${value || 'нет отметок'}. ${common.days[date].suffix}${total && common.days[date].gap ? ' ' + common.days[date].gapHint : ''}`,
                    className: `${common.days[date].className}${total && day.blank_weekday ? ' blank' : ''}${total && common.days[date].gap ? ' review-gap' : ''}`,
                    issue: Boolean(day.issues.length && (total || row.days[date]))};
            })});
        rows.set(item.key, result);
        return result;
    }
    function items(filtered) {
        return Object.freeze(filtered.reduce((result, group) => {
            if (!sourceOrders.has(group)) sourceOrders.set(group, orderedRows(group.rows.map(row => {
                const origin = source(row) || {};
                return {id: row.key, building_id: origin.building_id, object_name: origin.object_name,
                    timesheet_name: origin.name, available: row.available !== false, row,
                    entries: Object.entries(row.days).map(([work_date, day]) => ({work_date, worked_minutes: day.worked_minutes}))};
            })).map(item => item.row));
            sourceOrders.get(group).forEach(row => result.push(Object.freeze({key: `source:${row.key}`, group, row})));
            result.push(Object.freeze({key: `total:${group.key}`, group, row: null}));
            return result;
        }, []));
    }
    function options(id, name) {
        return Object.freeze(Array.from(new Map(grid.sources.map(value => [value[id], {id: value[id], name: value[name]}])).values())
            .sort((a, b) => a.name.localeCompare(b.name, 'ru')));
    }
    function gapMessage(group, date) { return gapHint(groupGaps(grid, group, sources).get(date), date, 'Доступные табели сотрудника'); }
    return Object.freeze({sources, columns, source, conflictSource, rowView, items, gapMessage,
        objects: options('building_id', 'object_name'), authors: Object.freeze(options('author_id', 'author_name').filter(value => value.id))});
}

module.exports = {createView, statuses, issueLabels, decimal, hours, compactName, authorLabel, rowValue, totalValue};
